import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from '@/App.jsx'
import '@/index.css'

// Sentry is only active when VITE_SENTRY_DSN is set.
// In local dev (no DSN), all Sentry calls are no-ops — zero overhead.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || '',
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,           // "production" | "development"
  integrations: [
    Sentry.browserTracingIntegration(),
    // Session replay only on errors — saves quota on free tier
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Capture 10% of page loads for performance monitoring
  tracesSampleRate: 0.1,
  // No random session replays; 100% replay when an error occurs
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
  // Sentry automatically catches unhandledrejection — no manual listener needed
});

// Register service worker for PWA support — production only.
// In development, the SW's cache-first strategy for JS assets conflicts with
// Vite's HMR, causing stale module instances and "dispatcher is null" errors.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
} else if ('serviceWorker' in navigator) {
  // Development: a service worker left over from a previous production-mode run on this
  // origin (localhost) keeps intercepting requests and serving stale cached chunks, which
  // shows up as "multiple copies of React" / "Invalid hook call". Unregister it and purge
  // its caches so dev always loads fresh modules from Vite.
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
  if (window.caches) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
  }
}

// Auto-reload on chunk load failure.
// After a new deploy, old JS chunks are removed from the server (rsync --delete).
// If a user has the old app open and lazy-navigates to a new route, the browser
// requests a chunk URL that no longer exists — Vite throws in two ways:
//   1. vite:preloadError — fired for <link rel="modulepreload"> failures (Vite 4+)
//   2. unhandledrejection — fired for dynamic import() failures
// Both reload the page so the user transparently gets the new index.html + chunks.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

window.addEventListener('unhandledrejection', (event) => {
  const msg = event?.reason?.message || '';
  if (msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Unable to preload CSS')) {
    event.preventDefault();
    window.location.reload();
  }
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <App />
)
